import { ReservationSourceSchema } from '@ooh/contracts';
import type { ReservationListItem as ContractItem } from '@ooh/contracts';
import type { FilterField } from '@ooh/ui/components/filter-panel';
import { pl } from 'date-fns/locale';
import type { TFunction } from 'i18next';
import type { ReservationListItem } from '../components/reservation-list-view';
import type { ReservationListParams } from '../api/queries';
import {
  buildReservationFocusParam,
  formatReservationSource,
  mapBaseReservationFields,
} from './formatters';

export interface ReservationsSearch {
  status: string[];
  holderUserId: string[];
  offerId: string[];
  mediaUnitId: string[];
  source: string[];
  clientId: string[];
  salespersonId: string[];
  dateFrom?: string;
  dateTo?: string;
  limit: number;
  offset: number;
  sort: 'date_from' | 'date_to' | 'created_at' | 'status';
  order: 'asc' | 'desc';
  hasQueue: boolean;
  /** Row whose queue should start expanded, e.g. from the unit panel's "Otwórz pełną kolejkę" deep link. */
  expand?: number;
}

export type ReservationsSearchChange = (
  patch: Partial<ReservationsSearch>,
  options?: { replace?: boolean },
) => void;

export interface ReservationFilterOption {
  value: string;
  label: string;
}

export interface ReservationFilterOptions {
  offers: ReservationFilterOption[];
  clients: ReservationFilterOption[];
  salespeople: ReservationFilterOption[];
}

export function buildReservationListQueryParams(search: ReservationsSearch): ReservationListParams {
  return {
    status: search.status.length > 0 ? search.status : undefined,
    holderUserId: search.holderUserId.length > 0 ? search.holderUserId.map(Number) : undefined,
    offerId: search.offerId.length > 0 ? search.offerId.map(Number) : undefined,
    mediaUnitId: search.mediaUnitId.length > 0 ? search.mediaUnitId.map(Number) : undefined,
    source: search.source.length > 0 ? search.source : undefined,
    clientId: search.clientId.length > 0 ? search.clientId.map(Number) : undefined,
    salespersonId: search.salespersonId.length > 0 ? search.salespersonId.map(Number) : undefined,
    dateFrom: search.dateFrom,
    dateTo: search.dateTo,
    limit: search.limit,
    offset: search.offset,
    sort: search.sort,
    order: search.order,
    hasQueue: search.hasQueue || undefined,
  };
}

export function buildReservationFilterValues(search: ReservationsSearch): Record<string, string[]> {
  return {
    status: search.status,
    offerId: search.offerId,
    clientId: search.clientId,
    salespersonId: search.salespersonId,
    source: search.source,
    // Bridge separate dateFrom/dateTo URL params to the dateRange pill's [from, to] format
    dateRange: [search.dateFrom ?? '', search.dateTo ?? ''],
  };
}

/**
 * `holderUserId` and `mediaUnitId` are valid URL/API params but intentionally
 * have no filter pill here: FilterPanel does not render 'text'-type fields,
 * so they never produced a visible control. Self-scoping by holder is covered
 * by the pinned "Moje rezerwacje" toggle instead.
 */
export function buildReservationFilterFields(
  t: TFunction<'reservations'>,
  offerOptions: ReservationFilterOption[],
  clientOptions: ReservationFilterOption[] = [],
  salespersonOptions: ReservationFilterOption[] = [],
): FilterField[] {
  return [
    {
      type: 'select',
      id: 'status',
      label: t('filters.status'),
      pinned: true,
      options: [
        { value: 'SOFT_RESERVATION', label: t('status.SOFT_RESERVATION') },
        { value: 'HARD_RESERVATION', label: t('status.HARD_RESERVATION') },
        { value: 'SOLD', label: t('status.SOLD') },
        { value: 'OPTION', label: t('status.OPTION') },
      ],
    },
    { type: 'select', id: 'offerId', label: t('filters.offerId'), options: offerOptions },
    { type: 'select', id: 'clientId', label: t('filters.clientId'), options: clientOptions },
    {
      type: 'select',
      id: 'salespersonId',
      label: t('filters.salespersonId'),
      options: salespersonOptions,
    },
    {
      type: 'select',
      id: 'source',
      label: t('filters.source'),
      options: ReservationSourceSchema.options.map((value) => ({
        value,
        label: t(`source.${value}`),
      })),
    },
    {
      type: 'dateRange',
      id: 'dateRange',
      label: `${t('filters.dateFrom')} \u2013 ${t('filters.dateTo')}`,
      locale: pl,
    },
  ];
}

/** Human label for a sort key, falling back to the raw key for forward-compat with new sort keys. */
export function formatSortFieldLabel(t: TFunction<'reservations'>, sortKey: string): string {
  return t(`sortFields.${sortKey}`, { defaultValue: sortKey });
}

export function makeReservationListItemMapper(
  t: (key: string, opts?: Record<string, unknown>) => string,
): (item: ContractItem) => ReservationListItem {
  return (item: ContractItem): ReservationListItem => ({
    ...mapBaseReservationFields(
      item,
      t(`status.${item.status}`),
      formatReservationSource(item.source, t),
    ),
    mediaUnitId: item.mediaUnitId,
    mediaUnitName: item.mediaUnitName,
    mediaUnitCity: '',
    mediaUnitCode: item.mediaUnitSupplierNumber ?? '',
    unitHref: `/media-units/${String(item.mediaUnitId)}?tab=reservations&focus=${buildReservationFocusParam(item.id)}`,
    targetVisibilityDays: item.targetVisibilityDays,
    waitingCount: item.waitingCount,
    clientName: item.clientName,
    salespersonName: item.salespersonName,
  });
}
